<script lang="ts" setup>
import { computed } from 'vue';
import { JobTravelExpectations } from '@factoryfixinc/ats-interfaces';

const props = withDefaults(
  defineProps<{
    modelValue: JobTravelExpectations | null;
    isDisabled?: boolean;
  }>(),
  { isDisabled: false },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: JobTravelExpectations): void;
}>();

// Kickoff/prototype wording, in the order the prototype shows them. Each option carries the
// explanation David wrote (2026-09-16), shown once it is selected.
const OPTIONS: { value: JobTravelExpectations; label: string; explanation: string }[] = [
  {
    value: JobTravelExpectations.DAY_TRIPS_HOME_MOST_NIGHTS,
    label: 'Day trips, home most nights',
    explanation:
      'Your AI recruiter will describe mostly same-day travel, with workers typically home at night.',
  },
  {
    value: JobTravelExpectations.OVERNIGHT_HOME_MOST_WEEKENDS,
    label: 'Overnight, home most weekends',
    explanation:
      'Your AI recruiter will describe overnight travel during the week, with workers typically ' +
      'home most weekends.',
  },
  {
    value: JobTravelExpectations.ASSIGNMENTS_1_3_MONTHS,
    label: 'Extended, 1–3 months per assignment',
    explanation:
      'Your AI recruiter will describe assignments that typically last one to three months.',
  },
  {
    value: JobTravelExpectations.ASSIGNMENTS_3_PLUS_MONTHS,
    label: 'Extended, 3+ months per assignment',
    explanation:
      'Your AI recruiter will describe assignments that typically last three months or longer.',
  },
  {
    value: JobTravelExpectations.VARIES,
    label: 'It varies',
    explanation:
      'Your AI recruiter will explain that the travel schedule varies, without promising a fixed ' +
      'pattern.',
  },
];

const selectedOption = computed(
  () => OPTIONS.find((option) => option.value === props.modelValue) ?? null,
);

const requiresAnswer = (value: JobTravelExpectations | null): boolean | string =>
  !!value || 'Choose how often the work is away overnight';

function select(value: JobTravelExpectations) {
  if (props.isDisabled) return;
  if (value === props.modelValue) return;
  emit('update:modelValue', value);
}
</script>

<template>
  <div
    data-test="travel-expectations-selector"
    :class="{ 'pointer-events-none opacity-60': isDisabled }"
  >
    <p class="mb-3 font-sans text-xs font-bold leading-[18px] text-shade-900">
      For the typical hire, how often is the work away overnight?*
    </p>
    <v-input
      :model-value="modelValue"
      :rules="[requiresAnswer]"
      validate-on="submit"
      hide-details="auto"
    >
      <!-- v-input lays its control out as a row; one column keeps the explanation under the pills. -->
      <div class="flex w-full flex-col">
        <div class="mb-3 flex flex-wrap gap-2">
          <button
            v-for="option in OPTIONS"
            :key="option.value"
            type="button"
            :data-test="`travel-expectation-${option.value}`"
            class="pill"
            :class="{ 'pill--selected': option.value === modelValue }"
            :aria-pressed="option.value === modelValue"
            :disabled="isDisabled"
            @click="select(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <p
          v-if="selectedOption"
          data-test="travel-expectation-explanation"
          class="-mt-1 mb-3 font-sans text-xs leading-[18px] text-shade-800"
        >
          {{ selectedOption.explanation }}
        </p>
      </div>
    </v-input>
  </div>
</template>

<style lang="postcss" scoped>
.pill {
  @apply inline-flex h-8 items-center justify-center rounded-full border border-tint-100;
  @apply whitespace-nowrap bg-white px-3 font-sans text-sm font-normal leading-5 text-shade-820;
  @apply transition-colors;
}

.pill:hover:not(:disabled):not(.pill--selected) {
  @apply border-shade-840;
}

.pill--selected {
  @apply border-highlight-600 bg-highlight-50 font-bold text-highlight-600;
}

.pill:disabled {
  @apply cursor-not-allowed;
}
</style>
